import React, { useEffect, useMemo, useState } from 'react';
import { Text, View, Platform, TouchableOpacity, ScrollView, StyleSheet, Linking } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import Svg, { Circle } from 'react-native-svg';
import * as XLSX from 'xlsx-js-style';

import { getPmcReportData, getPmcReportFilters } from '../../../api/Reservation';
import { getBrandsList } from '../../../api/Price';
import { BasicLayout, CommonContainer } from '../../../common/components/CustomLayout';
import { BOHToolbar, renderBOHTlbDatePicker, BOHButton, BOHTlbrSearchPicker } from '../../../common/components/bohtoolbar';
import MultiSelect from '../../../common/components/ui/MultiSelect';
import { useAlertModal } from '../../../common/hooks';
import { msgStr } from '../../../common/constants/Message';
import { TextdefaultSize, TextSmallSize } from '../../../common/constants/Fonts';
import { formatDate } from '../../../common/utils/DateUtils';
import { BASE_URL } from '../../../common/constants/AppConstants';

interface Props {
  navigation: any;
}

// stage values match the reservation's existing convention (see ReservationsList.tsx)
const STATUS_OPTIONS = [
  { label: 'Provisional', value: 1 },
  { label: 'Confirmed', value: 2 },
  { label: 'Checked Out', value: 3 },
  { label: 'Checked In', value: 4 },
  { label: 'Cancelled', value: 5 },
];
const DEFAULT_STATUSES = [1, 2, 3, 4]; // all except Cancelled

const currency = (n: number) =>
  (n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const pct = (n: number) => `${(n || 0).toFixed(1)}%`;

const signedPct = (n: number | null) => {
  if (n === null) return 'N/A';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
};

const signedCurrency = (n: number) => {
  const sign = n > 0 ? '+' : n < 0 ? '-' : '';
  return `${sign}${currency(Math.abs(n))}`;
};

// Same month/day shifted by `years` (negative = earlier), in 'YYYY-MM-DD' format.
// NOTE: deliberately avoids `new Date(dateStr)` — parsing a date-only ISO
// string treats it as UTC midnight, while getFullYear/setFullYear operate in
// local time, which silently rolls the date back a day in negative UTC
// offset timezones and corrupts the year math. Shifting the string directly
// sidesteps that entirely.
const shiftDateByYears = (dateStr: string, years: number) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return `${y + years}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};

const shiftYearBack = (dateStr: string) => shiftDateByYears(dateStr, -1);

// Compact filter dropdown — the underlying MultiSelect renders a chip per
// selected item both inline and in a duplicate list below by default, which
// stacks the whole page when most/all options are selected. We suppress both
// and show a short "N of M selected" / "All" summary instead.
interface FilterDropdownProps {
  label: string;
  options: string[] | { label: string; value: any }[];
  value: any[];
  onChange: (value: any[]) => void;
  search?: boolean;
}

const SELECT_ALL_VALUE = '__select_all__';

const FilterDropdown: React.FC<FilterDropdownProps> = ({ label, options, value, onChange, search }) => {
  const normalizedOptions = options.map((o: any) => (typeof o === 'string' ? { label: o, value: o } : o));
  const allSelected = normalizedOptions.length > 0 && value.length === normalizedOptions.length;
  const dropdownOptions = [
    { label: allSelected ? 'Unselect All' : 'Select All', value: SELECT_ALL_VALUE },
    ...normalizedOptions,
  ];
  const summary =
    value.length === 0
      ? 'None'
      : value.length === normalizedOptions.length
      ? 'All'
      : `${value.length} of ${normalizedOptions.length}`;

  // The underlying MultiSelect toggles selection by presence in `value`; since
  // the sentinel is never part of the real `value` we pass in, clicking it
  // always shows up as "added" here — we use that as the trigger and decide
  // select-all vs. unselect-all based on the current state instead.
  const handleChange = (newValue: any[]) => {
    if (newValue.includes(SELECT_ALL_VALUE)) {
      onChange(allSelected ? [] : normalizedOptions.map((o: any) => o.value));
    } else {
      onChange(newValue);
    }
  };

  return (
    <View style={{ position: 'relative', justifyContent: 'center' }}>
      <MultiSelect
        label=""
        options={dropdownOptions}
        value={value}
        onChange={handleChange}
        placeholder=" "
        search={search}
        searchPlaceholder={`Search ${label}...`}
        visibleSelectedItem={false}
        renderSelectedItem={() => null}
        renderItem={(item: any, selected?: boolean) => {
          if (item.value === SELECT_ALL_VALUE) {
            return (
              <View style={styles.filterSelectAllRow}>
                <FontAwesome5
                  name={allSelected ? 'check-square' : 'square'}
                  solid={allSelected}
                  size={14}
                  color={allSelected ? '#2e96e1' : '#999'}
                  style={{ marginRight: 10 }}
                />
                <Text style={styles.filterSelectAllText}>{item.label}</Text>
              </View>
            );
          }
          return (
            <View style={styles.filterItemRow}>
              <FontAwesome5
                name={selected ? 'check-square' : 'square'}
                solid={selected}
                size={14}
                color={selected ? '#2e96e1' : '#999'}
                style={{ marginRight: 10 }}
              />
              <Text style={selected ? styles.filterItemTextSelected : styles.filterItemText}>{item.label}</Text>
            </View>
          );
        }}
      />
      <View style={styles.filterOverlay} pointerEvents="none">
        <Text numberOfLines={1} style={styles.filterOverlayText}>{label}: {summary}</Text>
      </View>
    </View>
  );
};

const PIE_COLORS = [
  '#2e96e1', '#e15f2e', '#2ee178', '#c22ee1', '#e1c92e',
  '#2ecde1', '#e12e5f', '#7a2ee1', '#8ce12e', '#e1892e',
];

// Donut chart of PMC % makeup (by prediscounted $). Top 8 PMCs get their own
// slice; the rest are grouped into "Other" so the chart stays readable when
// there are dozens of PMCs.
const PmcPieChart: React.FC<{ pmcList: any[] }> = ({ pmcList }) => {
  if (!pmcList.length) return null;

  const TOP_N = 8;
  const top = pmcList.slice(0, TOP_N);
  const rest = pmcList.slice(TOP_N);
  const otherTotal = rest.reduce((sum, p) => sum + p.percent, 0);
  const slices = otherTotal > 0 ? [...top, { pmc: 'Other', percent: otherTotal }] : top;

  const size = 200;
  const strokeWidth = 34;
  const radius = (size - strokeWidth) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;

  let cumulative = 0;

  return (
    <View style={styles.pieContainer}>
      <Text style={styles.pieTitle}>PMC % Makeup</Text>
      <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
        <Svg width={size} height={size}>
          {slices.map((s, i) => {
            const segmentLength = (s.percent / 100) * circumference;
            const offset = -cumulative;
            cumulative += segmentLength;
            return (
              <Circle
                key={s.pmc}
                cx={cx}
                cy={cy}
                r={radius}
                stroke={PIE_COLORS[i % PIE_COLORS.length]}
                strokeWidth={strokeWidth}
                strokeDasharray={`${segmentLength} ${circumference - segmentLength}`}
                strokeDashoffset={offset}
                fill="transparent"
                rotation={-90}
                origin={`${cx}, ${cy}`}
              />
            );
          })}
        </Svg>
        <View style={{ marginLeft: 20 }}>
          {slices.map((s, i) => (
            <View key={s.pmc} style={styles.pieLegendRow}>
              <View style={[styles.pieLegendSwatch, { backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }]} />
              <Text style={styles.pieLegendText}>{s.pmc} — {pct(s.percent)}</Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
};

const COLS = {
  label: 220,
  discounted: 110,
  prediscounted: 120,
  percent: 90,
  yoyDiff: 110,
  yoyPct: 90,
  orders: 80,
  avgOrder: 110,
  bikes: 80,
  orderNumber: 100,
  createDate: 100,
  start: 90,
  end: 90,
  brand: 120,
  customer: 150,
  address: 220,
};

const PmcReport = ({ navigation }: Props) => {
  const { showAlert } = useAlertModal();

  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 1);

  const [searchOptions, setSearchOptions] = useState({
    start_date: formatDate(startOfYear),
    end_date: formatDate(today),
  });
  const [dateField, setDateField] = useState<'createdAt' | 'start_date' | 'end_date'>('start_date');

  const [pmcOptions, setPmcOptions] = useState<string[]>([]);
  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [selectedPmcs, setSelectedPmcs] = useState<string[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>(DEFAULT_STATUSES);
  const [filtersLoaded, setFiltersLoaded] = useState(false);

  const [rows, setRows] = useState<any[]>([]);
  const [priorYearRows, setPriorYearRows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [expandedPmc, setExpandedPmc] = useState<Record<string, boolean>>({});
  const [expandedBrand, setExpandedBrand] = useState<Record<string, boolean>>({});

  const changeSearchOptions = (key: string, val: any) => {
    setSearchOptions(prev => ({ ...prev, [key]: val }));
  };

  // Shifts both start_date and end_date by exactly one year in the given
  // direction, so the user can quickly flip between comparable years.
  const flipYear = (direction: -1 | 1) => {
    setSearchOptions(prev => ({
      ...prev,
      start_date: shiftDateByYears(prev.start_date, direction),
      end_date: shiftDateByYears(prev.end_date, direction),
    }));
  };

  // Load filter option lists once, then default everything to "all selected".
  useEffect(() => {
    getPmcReportFilters((jsonRes: string[], status: number) => {
      if (status === 200 && Array.isArray(jsonRes)) {
        setPmcOptions(jsonRes);
        setSelectedPmcs(jsonRes);
      }
    });
    getBrandsList({}, (jsonRes: any[], status: number) => {
      if (status === 200 && Array.isArray(jsonRes)) {
        const brands = jsonRes.map((b: any) => b.brand).filter(Boolean);
        setBrandOptions(brands);
        setSelectedBrands(brands);
      }
      setFiltersLoaded(true);
    });
  }, []);

  const getTable = () => {
    setIsLoading(true);
    getPmcReportData(
      {
        searchOptions: {
          start_date: searchOptions.start_date,
          end_date: searchOptions.end_date,
          date_field: dateField,
          pmc: selectedPmcs,
          brand: selectedBrands,
          stage: selectedStatuses,
        },
      },
      (jsonRes: any, status: number) => {
        switch (status) {
          case 200:
            setRows(jsonRes);
            break;
          case 500:
            showAlert('error', msgStr('serverError'));
            break;
          default:
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
            break;
        }
        setIsLoading(false);
      }
    );
  };

  const getPriorYearTable = () => {
    getPmcReportData(
      {
        searchOptions: {
          start_date: shiftYearBack(searchOptions.start_date),
          end_date: shiftYearBack(searchOptions.end_date),
          date_field: dateField,
          pmc: selectedPmcs,
          brand: selectedBrands,
          stage: selectedStatuses,
        },
      },
      (jsonRes: any, status: number) => {
        if (status === 200) setPriorYearRows(jsonRes);
        else setPriorYearRows([]);
      }
    );
  };

  useEffect(() => {
    if (!filtersLoaded) return;
    getTable();
    getPriorYearTable();
  }, [filtersLoaded, searchOptions, dateField, selectedPmcs, selectedBrands, selectedStatuses]);

  // ---- Rollup aggregation: PMC -> Brand -> Detail ----
  const report = useMemo(() => {
    const grand = { prediscounted: 0, discounted: 0, orders: 0, bikes: 0, avgOrder: 0 };
    const pmcMap = new Map<string, any>();

    rows.forEach((row: any) => {
      const prediscounted = Number(row.prediscounted_total) || 0;
      const discounted = Number(row.discounted_total) || 0;
      const bikes = Number(row.quantity) || 0;

      grand.prediscounted += prediscounted;
      grand.discounted += discounted;
      grand.orders += 1;
      grand.bikes += bikes;

      if (!pmcMap.has(row.pmc)) {
        pmcMap.set(row.pmc, { pmc: row.pmc, prediscounted: 0, discounted: 0, orders: 0, bikes: 0, brandMap: new Map() });
      }
      const pmcAgg = pmcMap.get(row.pmc);
      pmcAgg.prediscounted += prediscounted;
      pmcAgg.discounted += discounted;
      pmcAgg.orders += 1;
      pmcAgg.bikes += bikes;

      if (!pmcAgg.brandMap.has(row.brand)) {
        pmcAgg.brandMap.set(row.brand, { brand: row.brand, prediscounted: 0, discounted: 0, orders: 0, bikes: 0, detail: [] });
      }
      const brandAgg = pmcAgg.brandMap.get(row.brand);
      brandAgg.prediscounted += prediscounted;
      brandAgg.discounted += discounted;
      brandAgg.orders += 1;
      brandAgg.bikes += bikes;
      brandAgg.detail.push({ ...row, prediscounted, discounted, bikes, percentOfYear: 0 });
    });

    // sort PMCs by prediscounted $ desc, and compute percentages
    const pmcList = Array.from(pmcMap.values()).sort((a, b) => b.prediscounted - a.prediscounted);
    pmcList.forEach(pmcAgg => {
      pmcAgg.percent = grand.prediscounted ? (pmcAgg.prediscounted / grand.prediscounted) * 100 : 0;
      pmcAgg.avgOrder = pmcAgg.orders ? pmcAgg.prediscounted / pmcAgg.orders : 0;
      pmcAgg.brands = Array.from(pmcAgg.brandMap.values()).sort((a: any, b: any) => b.prediscounted - a.prediscounted);
      pmcAgg.brands.forEach((brandAgg: any) => {
        brandAgg.percent = pmcAgg.prediscounted ? (brandAgg.prediscounted / pmcAgg.prediscounted) * 100 : 0;
        brandAgg.avgOrder = brandAgg.orders ? brandAgg.prediscounted / brandAgg.orders : 0;
        brandAgg.detail.sort((a: any, b: any) => (a.start_date || '').localeCompare(b.start_date || ''));
        brandAgg.detail.forEach((d: any) => {
          d.percentOfYear = grand.prediscounted ? (d.prediscounted / grand.prediscounted) * 100 : 0;
        });
      });
    });

    grand.avgOrder = grand.orders ? grand.prediscounted / grand.orders : 0;

    // ---- YoY comparison: same PMC, same filters, same range shifted back 1 year ----
    const priorPmcTotals = new Map<string, number>();
    let priorGrandTotal = 0;
    priorYearRows.forEach((row: any) => {
      const prediscounted = Number(row.prediscounted_total) || 0;
      priorGrandTotal += prediscounted;
      priorPmcTotals.set(row.pmc, (priorPmcTotals.get(row.pmc) || 0) + prediscounted);
    });

    const computeYoy = (current: number, prior: number) => {
      const diff = current - prior;
      const pctChange = prior !== 0 ? (diff / prior) * 100 : current !== 0 ? null : 0;
      return { diff, pctChange };
    };

    pmcList.forEach(pmcAgg => {
      const prior = priorPmcTotals.get(pmcAgg.pmc) || 0;
      const { diff, pctChange } = computeYoy(pmcAgg.prediscounted, prior);
      pmcAgg.yoyPrior = prior;
      pmcAgg.yoyDiff = diff;
      pmcAgg.yoyPct = pctChange;
    });

    const grandYoy = computeYoy(grand.prediscounted, priorGrandTotal);
    (grand as any).yoyPrior = priorGrandTotal;
    (grand as any).yoyDiff = grandYoy.diff;
    (grand as any).yoyPct = grandYoy.pctChange;

    return { grand, pmcList };
  }, [rows, priorYearRows]);

  const togglePmc = (pmc: string) => setExpandedPmc(prev => ({ ...prev, [pmc]: !prev[pmc] }));
  const toggleBrand = (key: string) => setExpandedBrand(prev => ({ ...prev, [key]: !prev[key] }));

  // Opens the reservation detail screen in a new browser tab. Reuses the
  // 'reservationdetail' deep-link route already parsed by Home.tsx's initial
  // URL handler, so a fresh tab boots the app straight into that screen.
  const openReservationInNewTab = (reservationId: number) => {
    const url = `${BASE_URL}/reservationdetail?reservationId=${reservationId}`;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.open(url, '_blank');
    } else {
      Linking.openURL(url);
    }
  };

  const exportToSpreadsheet = () => {
    if (Platform.OS !== 'web') {
      showAlert('info', 'Export is only available on web platform');
      return;
    }
    try {
      const headers = [
        'PMC', 'Brand', 'Order #', 'Create Date', 'Start', 'End', 'Customer',
        'Discounted $', 'Prediscounted $', '% of Year', '# of Bikes', 'Address',
      ];
      const data = rows.map((row: any) => [
        row.pmc,
        row.brand,
        row.order_number,
        row.createdAt ? formatDate(new Date(row.createdAt)) : '',
        row.start_date,
        row.end_date,
        row.full_name,
        Number(row.discounted_total) || 0,
        Number(row.prediscounted_total) || 0,
        report.grand.prediscounted ? ((Number(row.prediscounted_total) || 0) / report.grand.prediscounted) * 100 : 0,
        Number(row.quantity) || 0,
        row.address,
      ]);

      const wb = XLSX.utils.book_new();
      const wsData = [headers, ...data];
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      ws['!cols'] = [
        { wch: 22 }, { wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
        { wch: 20 }, { wch: 14 }, { wch: 16 }, { wch: 10 }, { wch: 10 }, { wch: 30 },
      ];

      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + '1';
        if (!ws[address]) continue;
        ws[address].s = {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '2e96e1' } },
        };
      }

      XLSX.utils.book_append_sheet(wb, ws, 'PMC Report');

      const yoyHeaders = ['PMC', 'Current Prediscounted $', 'Prior Year Prediscounted $', 'YoY $ Change', 'YoY % Change'];
      const yoyData = report.pmcList.map((p: any) => [
        p.pmc, p.prediscounted, p.yoyPrior, p.yoyDiff, p.yoyPct === null ? 'N/A' : p.yoyPct,
      ]);
      yoyData.push(['TOTAL', report.grand.prediscounted, (report.grand as any).yoyPrior, (report.grand as any).yoyDiff, (report.grand as any).yoyPct === null ? 'N/A' : (report.grand as any).yoyPct]);
      const yoyWs = XLSX.utils.aoa_to_sheet([yoyHeaders, ...yoyData]);
      yoyWs['!cols'] = [{ wch: 22 }, { wch: 20 }, { wch: 22 }, { wch: 16 }, { wch: 14 }];
      const yoyRange = XLSX.utils.decode_range(yoyWs['!ref']);
      for (let C = yoyRange.s.c; C <= yoyRange.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + '1';
        if (!yoyWs[address]) continue;
        yoyWs[address].s = { font: { bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '2e96e1' } } };
      }
      XLSX.utils.book_append_sheet(wb, yoyWs, 'PMC YoY Summary');

      const timestamp = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `pmc_report_${timestamp}.xlsx`);
      showAlert('success', `Exported ${rows.length} records`);
    } catch (error) {
      console.error(error);
      showAlert('error', msgStr('unknownError'));
    }
  };

  const renderHeaderRow = () => (
    <View style={[styles.row, styles.headerRow]}>
      <Text style={[styles.cell, styles.headerText, { width: COLS.label }]}>PMC / Brand</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.discounted }, styles.right]}>Discounted $</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.prediscounted }, styles.right]}>Prediscounted $</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.percent }, styles.right]}>% Makeup</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.yoyDiff }, styles.right]}>YoY $ Chg</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.yoyPct }, styles.right]}>YoY %</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.orders }, styles.right]}># Orders</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.avgOrder }, styles.right]}>Avg Order $</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.bikes }, styles.right]}># Bikes</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.orderNumber }]}>Order #</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.createDate }]}>Create Date</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.start }]}>Start</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.end }]}>End</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.brand }]}>Brand</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.customer }]}>Customer</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.address }]}>Address</Text>
    </View>
  );

  const renderDetailRow = (d: any) => (
    <View style={[styles.row, { backgroundColor: '#fff' }]} key={`d-${d.id}`}>
      <Text style={[styles.cell, { width: COLS.label, paddingLeft: 56 }]} />
      <Text style={[styles.cell, { width: COLS.discounted }, styles.right]}>{currency(d.discounted)}</Text>
      <Text style={[styles.cell, { width: COLS.prediscounted }, styles.right]}>{currency(d.prediscounted)}</Text>
      <Text style={[styles.cell, { width: COLS.percent }, styles.right]}>{pct(d.percentOfYear)}</Text>
      <Text style={[styles.cell, { width: COLS.yoyDiff }, styles.right]} />
      <Text style={[styles.cell, { width: COLS.yoyPct }, styles.right]} />
      <Text style={[styles.cell, { width: COLS.orders }, styles.right]}>1</Text>
      <Text style={[styles.cell, { width: COLS.avgOrder }, styles.right]}>{currency(d.prediscounted)}</Text>
      <Text style={[styles.cell, { width: COLS.bikes }, styles.right]}>{d.bikes}</Text>
      <TouchableOpacity style={[styles.cell, { width: COLS.orderNumber }]} onPress={() => openReservationInNewTab(d.id)}>
        <Text style={styles.orderNumberLink}>{d.order_number}</Text>
      </TouchableOpacity>
      <Text style={[styles.cell, { width: COLS.createDate }]}>{d.createdAt ? new Date(d.createdAt).toLocaleDateString() : ''}</Text>
      <Text style={[styles.cell, { width: COLS.start }]}>{d.start_date}</Text>
      <Text style={[styles.cell, { width: COLS.end }]}>{d.end_date}</Text>
      <Text style={[styles.cell, { width: COLS.brand }]}>{d.brand}</Text>
      <Text style={[styles.cell, { width: COLS.customer }]}>{d.full_name}</Text>
      <Text style={[styles.cell, { width: COLS.address }]} numberOfLines={1}>{d.address}</Text>
    </View>
  );

  const renderBrandRow = (pmcName: string, brandAgg: any) => {
    const key = `${pmcName}||${brandAgg.brand}`;
    const isOpen = !!expandedBrand[key];
    return (
      <React.Fragment key={key}>
        <TouchableOpacity onPress={() => toggleBrand(key)}>
          <View style={[styles.row, { backgroundColor: '#f0f6fc' }]}>
            <View style={[styles.cell, { width: COLS.label, paddingLeft: 32, flexDirection: 'row', alignItems: 'center' }]}>
              <FontAwesome5 name={isOpen ? 'chevron-down' : 'chevron-right'} size={10} color="#555" style={{ marginRight: 8 }} />
              <Text>{brandAgg.brand || '(No Brand)'}</Text>
            </View>
            <Text style={[styles.cell, { width: COLS.discounted }, styles.right]}>{currency(brandAgg.discounted)}</Text>
            <Text style={[styles.cell, { width: COLS.prediscounted }, styles.right]}>{currency(brandAgg.prediscounted)}</Text>
            <Text style={[styles.cell, { width: COLS.percent }, styles.right]}>{pct(brandAgg.percent)}</Text>
            <Text style={[styles.cell, { width: COLS.yoyDiff }, styles.right]} />
            <Text style={[styles.cell, { width: COLS.yoyPct }, styles.right]} />
            <Text style={[styles.cell, { width: COLS.orders }, styles.right]}>{brandAgg.orders}</Text>
            <Text style={[styles.cell, { width: COLS.avgOrder }, styles.right]}>{currency(brandAgg.avgOrder)}</Text>
            <Text style={[styles.cell, { width: COLS.bikes }, styles.right]}>{brandAgg.bikes}</Text>
            <Text style={[styles.cell, { width: COLS.orderNumber }]} />
            <Text style={[styles.cell, { width: COLS.createDate }]} />
            <Text style={[styles.cell, { width: COLS.start }]} />
            <Text style={[styles.cell, { width: COLS.end }]} />
            <Text style={[styles.cell, { width: COLS.brand }]} />
            <Text style={[styles.cell, { width: COLS.customer }]} />
            <Text style={[styles.cell, { width: COLS.address }]} />
          </View>
        </TouchableOpacity>
        {isOpen && brandAgg.detail.map((d: any) => renderDetailRow(d))}
      </React.Fragment>
    );
  };

  const renderPmcRow = (pmcAgg: any) => {
    const isOpen = !!expandedPmc[pmcAgg.pmc];
    return (
      <React.Fragment key={pmcAgg.pmc}>
        <TouchableOpacity onPress={() => togglePmc(pmcAgg.pmc)}>
          <View style={[styles.row, { backgroundColor: '#e3edf7' }]}>
            <View style={[styles.cell, { width: COLS.label, flexDirection: 'row', alignItems: 'center' }]}>
              <FontAwesome5 name={isOpen ? 'chevron-down' : 'chevron-right'} size={12} color="#333" style={{ marginRight: 8 }} />
              <Text style={{ fontWeight: 'bold' }}>{pmcAgg.pmc}</Text>
            </View>
            <Text style={[styles.cell, { width: COLS.discounted, fontWeight: 'bold' }, styles.right]}>{currency(pmcAgg.discounted)}</Text>
            <Text style={[styles.cell, { width: COLS.prediscounted, fontWeight: 'bold' }, styles.right]}>{currency(pmcAgg.prediscounted)}</Text>
            <Text style={[styles.cell, { width: COLS.percent, fontWeight: 'bold' }, styles.right]}>{pct(pmcAgg.percent)}</Text>
            <Text style={[styles.cell, { width: COLS.yoyDiff, fontWeight: 'bold' }, styles.right, pmcAgg.yoyDiff > 0 ? styles.positive : pmcAgg.yoyDiff < 0 ? styles.negative : null]}>{signedCurrency(pmcAgg.yoyDiff)}</Text>
            <Text style={[styles.cell, { width: COLS.yoyPct, fontWeight: 'bold' }, styles.right, pmcAgg.yoyPct > 0 ? styles.positive : pmcAgg.yoyPct < 0 ? styles.negative : null]}>{signedPct(pmcAgg.yoyPct)}</Text>
            <Text style={[styles.cell, { width: COLS.orders, fontWeight: 'bold' }, styles.right]}>{pmcAgg.orders}</Text>
            <Text style={[styles.cell, { width: COLS.avgOrder, fontWeight: 'bold' }, styles.right]}>{currency(pmcAgg.avgOrder)}</Text>
            <Text style={[styles.cell, { width: COLS.bikes, fontWeight: 'bold' }, styles.right]}>{pmcAgg.bikes}</Text>
            <Text style={[styles.cell, { width: COLS.orderNumber }]} />
            <Text style={[styles.cell, { width: COLS.createDate }]} />
            <Text style={[styles.cell, { width: COLS.start }]} />
            <Text style={[styles.cell, { width: COLS.end }]} />
            <Text style={[styles.cell, { width: COLS.brand }]} />
            <Text style={[styles.cell, { width: COLS.customer }]} />
            <Text style={[styles.cell, { width: COLS.address }]} />
          </View>
        </TouchableOpacity>
        {isOpen && pmcAgg.brands.map((brandAgg: any) => renderBrandRow(pmcAgg.pmc, brandAgg))}
      </React.Fragment>
    );
  };

  const renderTotalsRow = () => (
    <View style={[styles.row, { backgroundColor: '#333' }]}>
      <Text style={[styles.cell, styles.headerText, { width: COLS.label, color: '#fff' }]}>TOTAL</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.discounted, color: '#fff' }, styles.right]}>{currency(report.grand.discounted)}</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.prediscounted, color: '#fff' }, styles.right]}>{currency(report.grand.prediscounted)}</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.percent, color: '#fff' }, styles.right]}>100.0%</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.yoyDiff, color: '#fff' }, styles.right]}>{signedCurrency((report.grand as any).yoyDiff)}</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.yoyPct, color: '#fff' }, styles.right]}>{signedPct((report.grand as any).yoyPct)}</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.orders, color: '#fff' }, styles.right]}>{report.grand.orders}</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.avgOrder, color: '#fff' }, styles.right]}>{currency(report.grand.avgOrder)}</Text>
      <Text style={[styles.cell, styles.headerText, { width: COLS.bikes, color: '#fff' }, styles.right]}>{report.grand.bikes}</Text>
      <Text style={[styles.cell, { width: COLS.orderNumber }]} />
      <Text style={[styles.cell, { width: COLS.createDate }]} />
      <Text style={[styles.cell, { width: COLS.start }]} />
      <Text style={[styles.cell, { width: COLS.end }]} />
      <Text style={[styles.cell, { width: COLS.brand }]} />
      <Text style={[styles.cell, { width: COLS.customer }]} />
      <Text style={[styles.cell, { width: COLS.address }]} />
    </View>
  );

  return (
    <BasicLayout
      navigation={navigation}
      goBack={() => navigation.navigate('Reports')}
      screenName={'PMC Report'}
      isLoading={isLoading}
    >
      <CommonContainer horizontal={false} style={{ width: '100%' }}>
        <BOHToolbar style={{ zIndex: 20, flexWrap: 'wrap' }}>
          <View style={styles.yearFlipper}>
            <TouchableOpacity onPress={() => flipYear(-1)} style={styles.yearFlipperBtn}>
              <FontAwesome5 name="chevron-left" size={12} color="#333" />
            </TouchableOpacity>
            <Text style={styles.yearFlipperText}>
              {searchOptions.start_date.split('-')[0]}
            </Text>
            <TouchableOpacity onPress={() => flipYear(1)} style={styles.yearFlipperBtn}>
              <FontAwesome5 name="chevron-right" size={12} color="#333" />
            </TouchableOpacity>
          </View>
          <Text style={{ marginRight: 8, fontSize: TextdefaultSize }}>Start</Text>
          {Platform.OS === 'web' &&
            renderBOHTlbDatePicker(searchOptions.start_date, (date) => {
              changeSearchOptions('start_date', formatDate(date));
            })}
          <Text style={{ marginHorizontal: 8, fontSize: TextdefaultSize }}>End</Text>
          {Platform.OS === 'web' &&
            renderBOHTlbDatePicker(searchOptions.end_date, (date) => {
              changeSearchOptions('end_date', formatDate(date));
            })}
          <BOHTlbrSearchPicker
            label="Filter By"
            boxStyle={{ marginLeft: 16 }}
            width={140}
            items={[
              { label: 'Create Date', value: 'createdAt' },
              { label: 'Start Date', value: 'start_date' },
              { label: 'End Date', value: 'end_date' },
            ]}
            selectedValue={dateField}
            onValueChange={(v: any) => setDateField(v)}
          />
          <View style={{ width: 200, marginLeft: 16 }}>
            <FilterDropdown
              label="PMC"
              options={pmcOptions}
              value={selectedPmcs}
              onChange={setSelectedPmcs}
              search
            />
          </View>
          <View style={{ width: 200, marginLeft: 8 }}>
            <FilterDropdown
              label="Brand"
              options={brandOptions}
              value={selectedBrands}
              onChange={setSelectedBrands}
              search
            />
          </View>
          <View style={{ width: 200, marginLeft: 8 }}>
            <FilterDropdown
              label="Status"
              options={STATUS_OPTIONS}
              value={selectedStatuses}
              onChange={setSelectedStatuses}
            />
          </View>
          <BOHButton
            label="Export"
            style={{ marginLeft: 16 }}
            onPress={exportToSpreadsheet}
          />
        </BOHToolbar>

        <ScrollView horizontal style={{ width: '100%' }}>
          <View>
            {renderHeaderRow()}
            {report.pmcList.map((pmcAgg: any) => renderPmcRow(pmcAgg))}
            {renderTotalsRow()}
          </View>
        </ScrollView>

        <PmcPieChart pmcList={report.pmcList} />
      </CommonContainer>
    </BasicLayout>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
  },
  headerRow: {
    backgroundColor: '#f5f5f5',
  },
  headerText: {
    fontWeight: 'bold',
    fontSize: TextSmallSize,
  },
  cell: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: TextSmallSize,
  },
  right: {
    textAlign: 'right',
  },
  positive: {
    color: '#1a8a3f',
  },
  yearFlipper: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 20,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 4,
    overflow: 'hidden',
  },
  yearFlipperBtn: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: '#f5f5f5',
  },
  yearFlipperText: {
    fontSize: TextdefaultSize,
    fontWeight: 'bold',
    paddingHorizontal: 10,
    minWidth: 48,
    textAlign: 'center',
  },
  negative: {
    color: '#c22222',
  },
  filterOverlay: {
    position: 'absolute',
    left: 12,
    right: 30,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
  },
  filterOverlayText: {
    fontSize: TextSmallSize,
    color: '#333',
  },
  filterItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  filterSelectAllRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    backgroundColor: '#fafafa',
  },
  filterSelectAllText: {
    fontSize: TextSmallSize,
    color: '#2e96e1',
    fontWeight: 'bold',
  },
  filterItemText: {
    fontSize: TextSmallSize,
    color: '#333',
  },
  orderNumberLink: {
    fontSize: TextSmallSize,
    color: '#2e96e1',
    textDecorationLine: 'underline',
  },
  filterItemTextSelected: {
    fontSize: TextSmallSize,
    color: '#2e96e1',
    fontWeight: 'bold',
  },
  pieContainer: {
    marginTop: 24,
    padding: 16,
    borderWidth: 1,
    borderColor: '#eee',
    borderRadius: 6,
  },
  pieTitle: {
    fontWeight: 'bold',
    fontSize: TextdefaultSize,
    marginBottom: 12,
  },
  pieLegendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  pieLegendSwatch: {
    width: 12,
    height: 12,
    borderRadius: 2,
    marginRight: 8,
  },
  pieLegendText: {
    fontSize: TextSmallSize,
  },
});

export default PmcReport;
